import axios from 'axios';
import { makeAutoObservable, action, observable } from 'mobx';
import { meAPI, snsLoginAPI, snsJoinAPI, emailJoinAPI, emailLoginAPI, leaveAPI, findPwdAPI, changePwdAPI, phoneChangeAPI, findIdAPI, verifyPasswordAPI, requesterSaveAPI } from '../api/login';
import { EmailRegExp, PhoneRegExp } from '../lib/util';
import { rotateVisitorKey, getVisitorKey } from '../components/chat/visitorKey';

// 소셜 로그인 설정 — 빌드 환경변수(REACT_APP_*)로 바꿀 수 있다. 콜백은 항상 현재 도메인의 /login
const KAKAO_JS_KEY = process.env.REACT_APP_KAKAO_JS_KEY || '15a47e163ff40cf9ac569dc7a94c7b79';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '614361534660-969uun7v7knjmldsolltncttv0jjmfoj.apps.googleusercontent.com';
const NAVER_CLIENT_ID = process.env.REACT_APP_NAVER_CLIENT_ID || 'qbS2hes5VHizgMLTehZw';
const loginCallbackUrl = () => window.location.origin + '/login';

export default class login {
  root;
  userInfo = null;
  naverAuth2 = null;
  userKey = null;
  phoneCheck = false;
  impUid = null;
  findId = null;
  googleClient = null;   // Google Identity Services 토큰 클라이언트 (관찰 대상에서 제외)
  googleToken = '';

  constructor(root) {
    this.root = root;
    makeAutoObservable(
      this, {
        adminInfo: observable,
        userKey: observable,
        snsLogin: action,
        emailLogin: action,
        meCall: action,
        googleClient: false,
        googleToken: false,
        naverAuth2: false,
      }
    )
  };

  meCall = async () => {
    try {
      const resp = await meAPI();
      const { data, status } = resp;
      if (status !== 200) throw status;

      this.userInfo = data;
    } catch (error) {
      console.log('%c##### Error-meCall: ', 'color: red', error);
    }
  };

  // 토큰 저장 뒤 첫 화면으로
  finishLogin = (token) => {
    localStorage.setItem('qwdmtk', token);
    window.location.href = "/";
  }

  //SNS가입 — 서버가 가입/로그인을 한 번에 처리해 토큰을 돌려준다(카카오 인가 코드는 1회용)
  snsJoin = async (params) => {
    try {
      const resp = await snsJoinAPI(params);
      const { status, data } = resp || {};
      if (data && data.token) {
        this.finishLogin(data.token);
        return;
      }
      if (status === 409 || status === 201) {
        this.snsLogin(params);
        return;
      }
      alert((data && data.message) || '소셜 로그인에 실패했습니다.');
    } catch (error) {
      console.log('%c##### Error-snsJoin: ', 'color: red', error);
    }
  }

  // SNS로그인
  snsLogin = async (params) => {
    try {
      const resp = await snsLoginAPI(params);
      const { data } = resp || {};
      if (!data || !data.token) {
        alert((data && data.message) || '소셜 로그인에 실패했습니다.');
        return;
      }
      this.finishLogin(data.token);
    } catch (error) {
      console.log('%c##### Error-snsLogin error: ', 'color: red', error);
    }
  }

  // kakao 로그인 — SDK v2는 팝업 로그인이 없어 카카오 인증 페이지로 이동한 뒤 /login?code= 으로 돌아온다
  kakaologin = () => {
    const K = window.Kakao;
    if (!K || !K.Auth) {
      alert('카카오 로그인을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    if (!K.isInitialized()) K.init(KAKAO_JS_KEY);
    K.Auth.authorize({ redirectUri: loginCallbackUrl(), state: 'kakao' });
  }

  // 카카오 인가 코드 콜백 — 로그인·아이디찾기 화면이 열릴 때 한 번 확인한다
  snsCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code || params.get('state') !== 'kakao') return false;
    window.history.replaceState(null, '', window.location.pathname);
    this.snsJoin({ code, redirectUri: loginCallbackUrl(), socialType: 'Kakao' });
    return true;
  }

  // 구글 로그인 — Google Identity Services 토큰 클라이언트를 구글 버튼(id=googleBtn)에 건다.
  // 스크립트가 비동기로 늦게 뜰 수 있어 잠시 재시도한다
  googleLoginInit = (retry = 0) => {
    const el = document.getElementById('googleBtn');
    if (!el) return;
    const g = window.google && window.google.accounts && window.google.accounts.oauth2;
    if (!g) {
      if (retry < 20) setTimeout(() => this.googleLoginInit(retry + 1), 250);
      return;
    }
    if (!this.googleClient) {
      this.googleClient = g.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        callback: (resp) => {
          if (!resp || !resp.access_token) return;
          this.googleToken = resp.access_token;
          this.snsJoin({ socialToken: resp.access_token, socialType: 'Google' });
        },
      });
    }
    el.onclick = () => this.googleClient.requestAccessToken({ prompt: '' });
  }

  // (구) gapi 방식 호환 — 호출하는 곳이 남아 있어도 토큰 클라이언트로 넘긴다
  googleLogin = () => {
    if (this.googleClient) this.googleClient.requestAccessToken({ prompt: '' });
  }

  // 네이버 로그인 — 콜백은 현재 도메인 /login (https)
  naverLogin = async () => {
    if (!window.naver || !window.naver.LoginWithNaverId) return;
    const naverAuth2 = new window.naver.LoginWithNaverId(
      {
        clientId: NAVER_CLIENT_ID,
        callbackUrl: loginCallbackUrl(),
        isPopup: false, /* 팝업을 통한 연동처리 여부 */
        callbackHandle: true,
        loginButton: {
          color: "green", type: 1, height: 60
        } /* 로그인 버튼의 타입을 지정 */
      }
    );
    naverAuth2.init();
    this.naverEvent(naverAuth2);
  }

  naverEvent = (naverAuth2) => {

    if (!naverAuth2) return;
    this.naverAuth2 = naverAuth2;
    const naver = new Promise((resolve) => {
      naverAuth2.getLoginStatus(function (status) {
        if (status) {
          resolve(naverAuth2.accessToken.accessToken);
        } else {
          console.log("callback 처리에 실패하였습니다.");
        }
      });
    });

    naver.then((res) => {
      this.snsJoin({ socialToken: res, socialType: 'Naver' });
    })
  }

  // 네이버 회원 탈퇴 / 로그아웃 (연동 해제는 서버 비밀키가 필요해 브라우저에서는 쓰지 않는다)
  naverLeaveAPI = (cTk) => {
    axios({
      url: `https://nid.naver.com/oauth2.0/token?grant_type=delete&client_id=${NAVER_CLIENT_ID}&access_token=${cTk}&service_provider=NAVER`,
    })
      .then((response) => {
        return response
      })
      .catch((error) => {
        return error.response;
      })
  }

  // 구글 회원 탈퇴 / 로그아웃
  googleLeave = (type) => {
    if (type === 'logout') {
      localStorage.clear();
      return;
    }
    const g = window.google && window.google.accounts && window.google.accounts.oauth2;
    if (g && this.googleToken) {
      g.revoke(this.googleToken, () => {});
      this.googleToken = '';
    }
  }

  // 카카오 회원탈퇴 / 로그아웃 — 인가 코드 방식이라 브라우저 SDK에 토큰이 없을 수 있다(있을 때만 처리)
  kakaoLeave = (type) => {
    const K = window.Kakao;
    if (!K || !K.Auth || !K.Auth.getAccessToken || !K.Auth.getAccessToken()) return;
    if (type === 'logout') {
      Promise.resolve(K.Auth.logout()).catch(() => {});
    } else {
      Promise.resolve(K.API.request({ url: '/v1/user/unlink' })).catch(() => {});
    }
  };

  // 로그아웃 토큰 삭제 및 회원정보 초기화
  logoutAndInitialUser = (type) => {
    const { userJoinType } = this.userInfo || {};
    if (userJoinType === "Naver") {
      // this.naverLeaveAPI(type);
      localStorage.removeItem('com.naver.nid.access_token');
      localStorage.removeItem('com.naver.nid.oauth.state_token');
    } else if (userJoinType === 'Kakao') {
      this.kakaoLeave(type);
    } else if (userJoinType === 'Google') {
      this.googleLeave(type);
    }

    localStorage.removeItem('qwdmtk');
    rotateVisitorKey();   // 실시간문의 방문객 키 교체 — 다음 사람이 이 회원의 대화를 보지 못하게
    this.userInfo = null;
  }

  // 회원탈퇴
  leaveCall = async (params, func) => {
    try {
      const resp = await leaveAPI(params);
      const { status, data } = resp || {};
      if (status !== 200) {
        if (data && data.message) alert(data.message);
        throw resp;
      }
      this.logoutAndInitialUser()
      func();
    } catch (error) {
      console.log('%c##### Error-levaeCall: ', 'color: red', error);
    }
  }

  phoneInitial = () => {
    this.phoneCheck = false;
    this.impUid = null;
  }

  // 회원 정보 갱신(법인카드 등록 등 서버가 user 를 돌려줄 때)
  setUserInfo = (user) => { if (user) this.userInfo = user; }

  // 탈퇴 1단계 — 비밀번호 확인. 소셜 회원은 서버가 required:false 로 바로 통과시킨다
  verifyPasswordCall = async (password) => {
    try {
      const resp = await verifyPasswordAPI({ password });
      const { data, status } = resp || {};
      if (status !== 200) return { ok: false, message: (data && data.message) || '비밀번호 확인에 실패했습니다.' };
      return { ok: true };
    } catch (error) {
      return { ok: false, message: '비밀번호 확인에 실패했습니다.' };
    }
  }

  // 의뢰자 정보 설정(마이페이지 모달) — 저장되면 회원 정보를 새 값으로 바꾼다
  saveRequesterCall = async (params) => {
    try {
      const resp = await requesterSaveAPI(params);
      const { data, status } = resp || {};
      if (status !== 200) return { ok: false, message: (data && data.message) || '저장하지 못했습니다.' };
      this.userInfo = data.user;
      return { ok: true };
    } catch (error) {
      return { ok: false, message: '저장하지 못했습니다.' };
    }
  }

  // 휴대폰 인증 완료 — 팝빌 문자 인증(PhoneVerify)이 돌려준 uid 를 가입·찾기 요청에 싣는다
  setPhoneVerified = (uid) => {
    this.impUid = uid || null;
    this.phoneCheck = Boolean(uid);
  }

  // 이메일 회원 가입
  emailJoin = async (params, history) => {
    const { email, password } = params;
    let msg = "";

    if (!this.phoneCheck) msg = "핸드폰 인증을 해주세요.";

    if (!email) msg = "이메일을 이력하세요.";
    else if (!password) msg = "비밀번호를 입력해주세요..";

    if (msg) {
      alert(msg);
      return;
    }

    params["uid"] = this.impUid;
    params["visitorKey"] = getVisitorKey();   // 기업 신청이면 직원 실시간문의 방을 이 브라우저와 잇는다

    try {
      const { bizCert, ...fields } = params;         // 사업자등록증 파일은 multipart 로 따로 실린다
      const resp = await emailJoinAPI(fields, bizCert);
      const { data, status } = resp;

      if (status === 202) { history.push({ pathname: '/signupsuccess', state: { applied: true } }); return; }
      if (status !== 201) throw resp;
      history.push('/signupsuccess');
    } catch (error) {
      if (error.data && error.data.message) {
        alert(error.data.message);
      }
      console.log('%c##### Error-emailJoin: ', 'color: red', error);
    }
  }

  // 이메일 로그인
  emailLogin = async (params, history) => {
    let msg = "";
    if (!params.email) msg = "이메일을 입력하세요.";
    else if (!params.password) msg = "비밀번호를 입력해주세요.";

    if (msg) {
      alert(msg);
      return;
    }

    try {
      const resp = await emailLoginAPI(params);
      const { data, status } = resp;

      if (status !== 200) {
        if (!data.message) throw status;
        else alert(data.message);

        throw status;
      }

      // 아이디 체크박스가 true면 저장
      if (params.saveId) {
        localStorage.setItem('qadmid', params.email);
      } else {
        localStorage.removeItem('qadmid');
      }

      await localStorage.setItem('qwdmtk', data.token);
      await this.meCall();

      history.push('/quickservice');

    } catch (error) {
      console.log('%c##### Error-emailLogin: ', 'color: red', error);
    }
  }

  findPwdCall = async (p, func) => {
    if (!this.impUid) {
      console.log("##### not impUid");
      return;
    }

    const params = {
      email: p.email,
      uid: this.impUid
    };

    try {
      const resp = await findPwdAPI(params);
      const { status, data } = resp;
      if (status !== 200) {
        if (data && data.message) alert(data.message);
        throw resp;
      }
      this.phoneInitial();
      this.userKey = data.userKey;
      func();
      console.log('%c##### debug-userKey: ', 'color: #058FD7', this.userKey);
    } catch (error) {
      console.log('%c##### Error-findPwdCall: ', 'color: red', error);
    }
  }

  changePwdCall = async (params, func) => {
    let msg = "";
    if (!params.password1 || !params.password) msg = "비밀번호를 입력해주세요..";
    if (params.password1 !== params.password) msg = "비밀번호가 일치하지 않습니다.";

    if (msg) {
      alert(msg);
      return;
    }

    try {
      params["userKey"] = this.userKey;

      const resp = await changePwdAPI(params);
      const { status, data } = resp;
      if (status !== 200) {
        if (data && data.message) alert(data.message);
        throw resp;
      }

      alert("변경되었습니다.");
      func();
    } catch (error) {
      console.log('%c##### Error-changePwdCall: ', 'color: red', error);
    }
  }

  // 핸드폰 번호 변경
  phoneChangeCall = async (func) => {
    if (!this.impUid) {
      console.log("##### not impUid");
      return;
    }

    const params = {
      uid: this.impUid
    };

    try {
      const resp = await phoneChangeAPI(params);
      const { status } = resp;
      if (status !== 200) throw resp;
      this.phoneInitial();
      func();
    } catch (error) {
      if (error.data && error.data.message) {
        alert(error.data.message);
      }
      console.log('%c##### Error-phoneChangeCall: ', 'color: red', error);
    }
  }

  findIdCall = async (func) => {
    if (!this.impUid) {
      console.log("##### not impUid");
      return;
    }

    const params = {
      uid: this.impUid
    };

    try {
      const resp = await findIdAPI(params);
      const { status, data } = resp;
      if (status !== 200) {
        if (data && data.message) alert(data.message);
        throw resp;
      }
      this.findId = data.email;
      this.phoneInitial();
      func();

    } catch (error) {
      console.log('%c##### Error-findIdCall: ', 'color: red', error);
    }

  }
}
